import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, Sparkles, Loader2 } from 'lucide-react';
import { queryEnterpriseDataFunction } from '../firebase';
import logger from '../utils/logger';

/**
 * standalone AI Panel component to isolate input state and prevent full dashboard re-renders.
 * optimized for high-performance, zero-latency typing.
 */
const AIAnalyticsPanel = React.memo(({ 
  isOpen, 
  onClose, 
  darkMode, 
  selectedFacility, 
  visibleFacilities = [], 
  chartData = [], 
  allTargets = [] 
}) => {
  const [aiQuery, setAiQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [isQueryingAI, setIsQueryingAI] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isQueryingAI]);

  const handleAskAI = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim() || isQueryingAI) return;
    
    const userQuery = aiQuery;
    setAiQuery('');
    setMessages(prev => [...prev, { role: 'user', content: userQuery }]);
    setIsQueryingAI(true);
    
    try {
      const payload = {
        question: userQuery,
        contextData: {
          facilityScope: selectedFacility,
          totalFacilities: (visibleFacilities || []).length,
          last12MonthsData: (chartData || []).slice(-12),
          activeTargets: (allTargets || []).filter(t => t.facility === selectedFacility || selectedFacility === 'all'),
          chatHistory: messages.slice(-5) // Pass small context of recent messages
        }
      };
      
      const res = await queryEnterpriseDataFunction(payload);
      setMessages(prev => [...prev, { role: 'ai', content: res.data.answer }]);
    } catch (err) {
      logger.error('Failed to query AI:', err);
      setMessages(prev => [...prev, { role: 'error', content: 'Sorry, an error occurred while connecting to the AI system. Please try again.' }]);
    } finally {
      setIsQueryingAI(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed bottom-6 right-6 w-80 sm:w-96 rounded-xl shadow-2xl flex flex-col border z-50 transition-all animate-in slide-in-from-bottom-4 duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ maxHeight: 'calc(100vh - 120px)' }}>
      {/* Header */}
      <div className={`p-3 border-b flex justify-between items-center rounded-t-xl ${darkMode ? 'bg-indigo-900/40 border-gray-700' : 'bg-indigo-50 border-indigo-100'}`}>
        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
          <MessageSquare className="h-4 w-4" />
          <h3 className="font-bold text-sm">Ask AI Analyst</h3>
        </div>
        <button onClick={onClose} className={`p-1 rounded-md transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-indigo-100 text-gray-500'}`}>
          <X className="h-4 w-4" />
        </button>
      </div>
      
      {/* Messages Area */}
      <div 
        ref={scrollRef}
        className="flex-1 p-4 overflow-y-auto text-sm scroll-smooth min-h-[300px] flex flex-col gap-4"
      >
        {messages.length === 0 && !isQueryingAI && (
          <div className={`h-full flex flex-col items-center justify-center text-center px-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <Sparkles className="h-8 w-8 mb-2 opacity-20" />
            <p className="text-xs">Ask a question about your utility performance, carbon targets, or site variances.</p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-3 rounded-2xl ${
              msg.role === 'user' 
                ? 'bg-indigo-600 text-white rounded-tr-none' 
                : msg.role === 'error'
                  ? 'bg-red-100 text-red-700 border border-red-200 rounded-tl-none'
                  : (darkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200') + ' rounded-tl-none border'
            }`}>
              <div className={`prose prose-sm w-full leading-relaxed ${msg.role === 'user' ? 'text-white' : (darkMode ? 'text-gray-200' : 'text-gray-800')} whitespace-pre-wrap`}>
                {msg.content}
              </div>
            </div>
          </div>
        ))}

        {isQueryingAI && (
          <div className="flex justify-start">
            <div className={`p-3 rounded-2xl rounded-tl-none border flex items-center gap-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-indigo-400' : 'bg-gray-100 border-gray-200 text-indigo-600'}`}>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs font-medium text-gray-500">AI is thinking...</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Input Form */}
      <form onSubmit={handleAskAI} className={`p-3 border-t flex gap-2 ${darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-100 bg-gray-50'} rounded-b-xl`}>
        <input 
          type="text" 
          placeholder="Why did gas costs rise in Dec?"
          value={aiQuery}
          onChange={(e) => setAiQuery(e.target.value)}
          className={`flex-1 px-3 py-2 text-sm rounded-lg border shadow-inner outline-none transition-all ${darkMode ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-500' : 'bg-white border-gray-300 text-gray-800 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400'}`}
          disabled={isQueryingAI}
          autoFocus
        />
        <button 
          type="submit" 
          disabled={isQueryingAI || !aiQuery.trim()}
          className={`p-2 rounded-lg transition-all ${isQueryingAI || !aiQuery.trim() ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50' : 'bg-indigo-600 text-white shadow-md hover:bg-indigo-700 active:scale-95'}`}
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
});

export default AIAnalyticsPanel;
